import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle2, Circle, Clock, AlertTriangle, Plus, X, 
  Calendar, Flag, Trash2, Edit2, ChevronDown, ChevronUp,
  ListTodo, CheckCheck, Timer, AlertCircle, Mail, Bell
} from 'lucide-react';
import { format, formatDistanceToNow, isToday, isTomorrow, isPast, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red text-white', icon: AlertTriangle },
  high: { label: 'High', color: 'bg-orange-500 text-white', icon: Flag },
  medium: { label: 'Medium', color: 'bg-yellow-500 text-white', icon: Flag },
  low: { label: 'Low', color: 'bg-gray-400 text-white', icon: Flag }
};

const STATUS_CONFIG = {
  todo: { label: 'To Do', color: 'bg-gray-100 text-gray-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-light-blue/20 text-light-blue', icon: Timer },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-gray-200 text-gray-500', icon: X }
};

const CATEGORY_OPTIONS = [
  { value: 'registration', label: 'Registration' },
  { value: 'payment', label: 'Payment' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'communication', label: 'Communication' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'general', label: 'General' }
];

const AdminTasksWidget = ({ expanded = false, onExpandChange }) => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'medium',
    due_date: ''
  });

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [showCompleted]);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/tasks`, {
        params: { include_completed: showCompleted },
        withCredentials: true
      });
      setTasks(res.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/tasks-stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch task stats:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return;
    
    try {
      const payload = {
        ...newTask,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
      };
      await axios.post(`${API_URL}/api/admin/tasks`, payload, { withCredentials: true });
      setNewTask({ title: '', description: '', category: 'general', priority: 'medium', due_date: '' });
      setShowAddTask(false);
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to create task');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/tasks/${taskId}`, { withCredentials: true });
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleSendReminders = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/tasks/trigger-reminders`, {}, { withCredentials: true });
      const results = res.data.results;
      if (results.overdue_sent > 0 || results.due_today_sent > 0) {
        alert(`Email reminders sent!\n- Overdue tasks: ${results.overdue_sent}\n- Due today: ${results.due_today_sent}`);
      } else if (results.errors.length > 0) {
        alert(`Could not send reminders:\n${results.errors.join('\n')}\n\nNote: Email service requires Resend API key.`);
      } else {
        alert('No tasks need reminders right now.');
      }
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders. Please try again.');
    }
  };

  const getDueDateDisplay = (dueDate) => {
    if (!dueDate) return null;
    const date = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
    
    if (isToday(date)) return { text: 'Today', className: 'text-orange-600 font-medium' };
    if (isTomorrow(date)) return { text: 'Tomorrow', className: 'text-yellow-600' };
    if (isPast(date)) return { text: `Overdue: ${format(date, 'MMM d')}`, className: 'text-red font-medium' };
    return { text: format(date, 'MMM d'), className: 'text-gray-500' };
  };

  const TaskItem = ({ task }) => {
    const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
    const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.todo;
    const StatusIcon = statusConfig.icon;
    const dueDisplay = getDueDateDisplay(task.due_date);
    const isCompleted = task.status === 'completed' || task.status === 'cancelled';

    return (
      <div 
        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all ${
          task.is_overdue ? 'border-red/30 bg-red/5' : 
          task.priority === 'urgent' ? 'border-orange-200 bg-orange-50/50' :
          'border-gray-100 hover:border-gray-200 bg-white'
        } ${isCompleted ? 'opacity-60' : ''}`}
        data-testid={`task-item-${task.task_id}`}
      >
        {/* Status Toggle */}
        <button
          onClick={() => handleUpdateStatus(task.task_id, isCompleted ? 'todo' : 'completed')}
          className={`mt-0.5 flex-shrink-0 ${isCompleted ? 'text-green-500' : 'text-gray-400 hover:text-green-500'}`}
          data-testid={`task-toggle-${task.task_id}`}
        >
          <StatusIcon className="w-5 h-5" />
        </button>

        {/* Task Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`font-medium text-sm ${isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}`}>
              {task.title}
            </h4>
            <div className="flex items-center space-x-1 flex-shrink-0">
              <Badge className={`text-xs ${priorityConfig.color}`}>
                {priorityConfig.label}
              </Badge>
            </div>
          </div>
          
          {task.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-1">{task.description}</p>
          )}
          
          <div className="flex items-center space-x-3 mt-2">
            {dueDisplay && (
              <span className={`text-xs flex items-center ${dueDisplay.className}`}>
                <Calendar className="w-3 h-3 mr-1" />
                {dueDisplay.text}
              </span>
            )}
            <Badge variant="outline" className="text-xs">
              {CATEGORY_OPTIONS.find(c => c.value === task.category)?.label || task.category}
            </Badge>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1 flex-shrink-0">
          {!isCompleted && task.status === 'todo' && (
            <button
              onClick={() => handleUpdateStatus(task.task_id, 'in_progress')}
              className="p-1 text-gray-400 hover:text-light-blue"
              title="Start task"
            >
              <Timer className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleDeleteTask(task.task_id)}
            className="p-1 text-gray-400 hover:text-red"
            title="Delete task"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="admin-tasks-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-lg">
            <ListTodo className="w-5 h-5 mr-2 text-navy" />
            My Tasks
          </CardTitle>
          <div className="flex items-center space-x-2">
            {(stats?.overdue > 0 || stats?.due_today > 0) && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSendReminders}
                className="text-xs"
                title="Send email reminders for due/overdue tasks"
                data-testid="send-reminders-btn"
              >
                <Bell className="w-3 h-3 mr-1" />
                Send Reminders
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setShowAddTask(true)}
              className="bg-navy hover:bg-navy/90"
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Task
            </Button>
          </div>
        </div>
        
        {/* Stats Row */}
        {stats && (
          <div className="flex items-center space-x-4 mt-3 text-sm">
            {stats.overdue > 0 && (
              <div className="flex items-center text-red">
                <AlertCircle className="w-4 h-4 mr-1" />
                <span className="font-medium">{stats.overdue} overdue</span>
              </div>
            )}
            {stats.due_today > 0 && (
              <div className="flex items-center text-orange-600">
                <Clock className="w-4 h-4 mr-1" />
                <span>{stats.due_today} due today</span>
              </div>
            )}
            {stats.urgent > 0 && (
              <div className="flex items-center text-red">
                <AlertTriangle className="w-4 h-4 mr-1" />
                <span>{stats.urgent} urgent</span>
              </div>
            )}
            <div className="flex items-center text-gray-500">
              <CheckCheck className="w-4 h-4 mr-1" />
              <span>{stats.completed} completed</span>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Add Task Form */}
        {showAddTask && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="Task title *"
              className="bg-white"
              data-testid="task-title-input"
            />
            <Textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="bg-white"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Due Date</label>
                <Input
                  type="datetime-local"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="bg-white"
                  data-testid="task-due-date"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Category</label>
                <select
                  value={newTask.category}
                  onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Priority</label>
              <div className="flex space-x-2">
                {['low', 'medium', 'high', 'urgent'].map(p => (
                  <button
                    key={p}
                    onClick={() => setNewTask({ ...newTask, priority: p })}
                    className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors ${
                      newTask.priority === p 
                        ? PRIORITY_CONFIG[p].color 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`priority-${p}`}
                  >
                    {PRIORITY_CONFIG[p].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddTask(false);
                  setNewTask({ title: '', description: '', category: 'general', priority: 'medium', due_date: '' });
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreateTask}
                disabled={!newTask.title.trim()}
                className="bg-navy hover:bg-navy/90"
                data-testid="save-task-btn"
              >
                Create Task
              </Button>
            </div>
          </div>
        )}

        {/* Tasks List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No tasks to show</p>
            </div>
          ) : (
            tasks.map(task => (
              <TaskItem key={task.task_id} task={task} />
            ))
          )}
        </div>

        {/* Show/Hide Completed Toggle */}
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className="w-full py-2 text-sm text-gray-500 hover:text-navy flex items-center justify-center"
        >
          {showCompleted ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Hide Completed
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Show Completed ({stats?.completed || 0})
            </>
          )}
        </button>
      </CardContent>
    </Card>
  );
};

export default AdminTasksWidget;
